// ════════════════════════════════════════════════════════════════════
// SOA  (Scope of Sales Appointment Confirmation Form)
//
// Canonical CMS MED-136 form. Used for any Medicare sales appointment.
// CMS requires the form be documented BEFORE the appointment except
// in same-day-exception circumstances.
//
// FLOW
// ──────────────────────────────────────────────────────────────────
//   1. Agent composes SOA → status='pending_client', magic-link
//      sent via SMS/email.
//   2. Client opens /soa/<token>, checks product boxes, signs.
//      status → 'pending_agent'. Server captures IP + UA.
//   3. Agent sees Health Portal banner "Client signed — needs your
//      countersign". Opens detail, signs, sets plans_represented +
//      appointment_completed_date. status → 'completed'.
//   4. Print-to-PDF copy filed in the agency compliance archive.
//
// SOURCE
// ──────────────────────────────────────────────────────────────────
// All form text below is reproduced VERBATIM from CMS MED-136
// (revision 08/28/18). DO NOT REWORD — CMS audits check for the
// canonical language. Even the punctuation matters.
// ════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';

// ── Form intro / disclosure body ────────────────────────────────
export const SOA_DISCLOSURE = `The Centers for Medicare and Medicaid Services (CMS) requires agents to document the scope of a marketing appointment prior to any face-to-face sales meeting to ensure understanding of what will be discussed between the agent and the Medicare beneficiary (or their authorized representative). All information provided on this form is confidential and should be completed by each person with Medicare or his/her authorized representative.

Please mark beside the type of product(s) you want the agent to discuss.`;

// ── Sign-block body (under the product checkboxes) ──────────────
export const SOA_SIGNATURE_BLOCK = `By signing this form, you agree to a meeting with a sales agent to discuss the types of products you initialed above. Please note, the person who will discuss the products is either employed or contracted by a Medicare plan. They do not work directly for the Federal government. This individual may also be paid based on your enrollment in a plan.

Signing this form does NOT obligate you to enroll in a plan, affect your current enrollment, or enroll you in a Medicare plan.`;

// ── Product catalog (verbatim CMS language for each product) ────
// Grouped by section header, matching the canonical form layout.
export const SOA_PRODUCT_CATALOG = [
  {
    section: 'Medicare Advantage Prescription Drug Plans (Part C) and Cost Plans',
    items: [
      {
        id: 'ma_ppo',
        label: 'Medicare Preferred Provider Organization (PPO) Plan',
        description:
          'A Medicare Advantage Plan that provides all Original Medicare Part A and Part B health coverage and includes Part D prescription drug coverage. PPOs have network doctors and hospitals but you can also use out-of-network providers, usually at a higher cost.',
      },
      {
        id: 'ma_hmo',
        label: 'Medicare Health Maintenance Organization (HMO) Plan',
        description:
          'A Medicare Advantage Plan that provides all Original Medicare Part A and Part B health coverage and includes Part D prescription drug coverage. With most HMOs, you can only get your care from doctors or hospitals in the plan’s network (except in emergencies).',
      },
    ],
  },
  {
    section: 'Stand-alone Medicare Prescription Drug Plans (Part D)',
    items: [
      {
        id: 'pdp',
        label: 'Medicare Prescription Drug Plan (PDP)',
        description:
          'A stand-alone drug plan that adds prescription drug coverage to Original Medicare, some Medicare Cost Plans, some Medicare Private-Fee-for-Service Plans, and Medicare Medical Savings Account Plans.',
      },
    ],
  },
  {
    section: 'Additional Products',
    items: [
      { id: 'dental_vision', label: 'Dental/Vision',                       description: '' },
      { id: 'medigap',        label: 'Medicare Supplement (Medigap) Products', description: '' },
    ],
  },
];

// Flat map id → label for the agent-side compose + receipt rendering.
export const SOA_PRODUCT_LABEL = SOA_PRODUCT_CATALOG
  .flatMap(s => s.items)
  .reduce((acc, p) => ({ ...acc, [p.id]: p.label }), {});

// CMS contact-method enum (canonical form field).
export const SOA_CONTACT_METHODS = [
  { id: 'in_person', label: 'In person'   },
  { id: 'phone',     label: 'Phone'       },
  { id: 'video',     label: 'Video'       },
  { id: 'walk_in',   label: 'Walk-in'     },
  { id: 'other',     label: 'Other'       },
];

// CMS retention note shown on the printed receipt.
export const SOA_RETENTION_NOTE =
  '*Scope of Appointment (SOA) documentation is subject to CMS record retention requirements*';

// MED-136 form identifier shown in the form footer.
export const SOA_FORM_ID = 'MED-136 (08/28/18)';

// ── Token + URL helpers (mirrors lib/consent.js) ────────────────
const BASE_URL = () => {
  try { return window.location.origin; } catch { return ''; }
};

export function generateSoaToken() {
  const bytes = new Uint8Array(16);
  (globalThis.crypto || window.crypto).getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

export function buildSoaUrl(token) {
  return `${BASE_URL()}/soa/${token}`;
}

// ── Date helpers ────────────────────────────────────────────────
export function formatSoaSignDateLong(d) {
  const date = d ? new Date(d) : new Date();
  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
  const day     = date.getDate();
  const month   = date.toLocaleDateString('en-US', { month: 'long' });
  const year    = date.getFullYear();
  const hh      = String(date.getHours()).padStart(2, '0');
  const mm      = String(date.getMinutes()).padStart(2, '0');
  return `${dayName}, ${day} ${month} ${year} ${hh}:${mm}`;
}

// ── Create a new SOA + send the magic link ──────────────────────
// Inserts the row at compose time. signature_data_url + signed_at
// stay NULL until the client signs via /soa/<token>.
export async function createSoa({
  ownerId,
  clientId = null,
  beneficiaryName,
  beneficiaryAddress = null,
  beneficiaryPhone = null,
  beneficiaryEmail = null,
  products = [],
  agentName,
  agentNpn,
  contactMethod = null,
  appointmentAt = null,
  sentVia = 'sms',
  expiresInDays = 30,
}) {
  if (!ownerId)         throw new Error('ownerId required.');
  if (!beneficiaryName) throw new Error('beneficiaryName required.');
  if (!agentName)       throw new Error('agentName required.');
  if (!agentNpn)        throw new Error('agentNpn required.');

  const token   = generateSoaToken();
  const expires = new Date(Date.now() + expiresInDays * 86400000).toISOString();

  const row = {
    owner_id:            ownerId,
    client_id:           clientId,
    token,
    status:              'pending_client',
    beneficiary_name:    beneficiaryName,
    beneficiary_address: beneficiaryAddress,
    beneficiary_phone:   beneficiaryPhone,
    beneficiary_email:   beneficiaryEmail,
    products,
    agent_name:          agentName,
    agent_npn:           agentNpn,
    contact_method:      contactMethod,
    disclosure_text:     SOA_DISCLOSURE,
    appointment_at:      appointmentAt,
    sent_via:            sentVia,
    sent_at:             new Date().toISOString(),
    sent_to_phone:       beneficiaryPhone,
    sent_to_email:       beneficiaryEmail,
    expires_at:          expires,
  };

  const { data, error } = await supabase
    .from('soa_records')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return { ...data, signUrl: buildSoaUrl(token) };
}

// ── Agent's countersign (second signature on a pending_agent row) ─
export async function agentCountersign({
  soaId,
  signatureDataUrl,
  plansRepresented = null,
  appointmentCompletedDate = null,
  planUseOnly = null,
}) {
  if (!soaId)            throw new Error('soaId required.');
  if (!signatureDataUrl) throw new Error('signatureDataUrl required.');

  const { error } = await supabase
    .from('soa_records')
    .update({
      status:                     'completed',
      agent_signature_data_url:   signatureDataUrl,
      agent_signed_at:            new Date().toISOString(),
      plans_represented:          plansRepresented,
      appointment_completed_date: appointmentCompletedDate,
      plan_use_only:              planUseOnly,
    })
    .eq('id', soaId);
  if (error) throw error;
  return true;
}

// ── Owner reads ──────────────────────────────────────────────────
export async function fetchOwnerSoas({ status = null, limit = 200 } = {}) {
  let q = supabase.from('soa_records').select('*').order('created_at', { ascending: false }).limit(limit);
  if (status) q = q.eq('status', status);
  const { data } = await q;
  return data || [];
}

export async function sweepExpiredSoas() {
  const { data } = await supabase.rpc('expire_stale_soas');
  return Number(data) || 0;
}

// Mark "I've seen the client signed" so the Health Portal banner
// can stop highlighting it.
export async function acknowledgeClientSigned(soaId) {
  if (!soaId) return false;
  const { error } = await supabase
    .from('soa_records')
    .update({ client_signed_notified_at: new Date().toISOString() })
    .eq('id', soaId);
  return !error;
}

// ── Browser fingerprint + geo helpers (reused from consent) ─────
// Soa needs the same proofs as consent, so we just re-use those.
export { deviceFingerprint, captureGeolocation } from './consent';
